// frontend/src/components/Navigation.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { usePresentation } from '../context/PresentationContext';
import logo from '../assets/constellia.png';
import './Navigation.css';

const Navigation = ({ farms: propFarms, selectedFarm: propSelected, onSelectFarm }) => {
  const navigate = useNavigate();
  const { farmId } = useParams();

  const { user, setUser } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const { mode, setMode } = usePresentation();

  const [farms, setFarms] = useState(propFarms || []);
  const [selectedFarm, setSelectedFarm] = useState(propSelected || farmId || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch farms internally if not provided via props
  useEffect(() => {
    if (propFarms && propFarms.length > 0) {
      setFarms(propFarms);
      return;
    }

    const fetchFarms = async () => {
      try {
        const res = await axios.get('/api/farms', { withCredentials: true });
        const list = res.data.farms || res.data || [];
        setFarms(list);
        console.log("[Navigation] Got farms:", farms)
        if (list.length > 0 && !selectedFarm) {
          setSelectedFarm(list[0].id);
        }
      } catch (err) {
        console.warn('Farms not yet configured or fetch failed in Navigation:', err.message);
        setFarms([]);
      }
    };

    if (user) {
      fetchFarms();
    }
  }, [user, propFarms]);

  // Sync state if URL contains farmId parameter
  useEffect(() => {
    if (farmId) {
      setSelectedFarm(farmId);
    }
  }, [farmId]);

  const handleFarmChange = (e) => {
    const newFarmId = e.target.value;
    setSelectedFarm(newFarmId);

    if (onSelectFarm) {
      onSelectFarm(newFarmId);
    } else {
      navigate(`/dashboard/farm/${newFarmId}`);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout', {}, { withCredentials: true });
      setUser(null);
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put('/auth/me/preferences', { language: lang }, { withCredentials: true });
    } catch (err) {
      console.error('Failed to update language preference:', err);
    }
  };

  const handleModeChange = async (newMode) => {
    setMode(newMode);
    try {
      await axios.put('/auth/me/preferences', { presentationMode: newMode }, { withCredentials: true });
    } catch (err) {
      console.error('Failed to update mode preference:', err);
    }
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        <Link to="/dashboard" className="nav-brand">
          <div className="nav-logo-badge">
            <img src={logo} alt="Constellia Logo" className="nav-logo" />
          </div>
        </Link>

        <div className="nav-actions">
          {/* Farm Switcher Dropdown */}
          {farms.length > 0 && (
            <div className="nav-farm-selector">
              <select
                id="nav-farm-select"
                value={selectedFarm || ''}
                onChange={handleFarmChange}
                className="nav-farm-dropdown"
                aria-label={t('dashboard.selectFarm') || 'Select Farm'}
              >
                {farms.map((farm) => (
                  <option key={farm.id} value={farm.id}>
                    🏡 {farm.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* User Email Dropdown Menu */}
          <div className="nav-user-container" ref={dropdownRef}>
            <button
              type="button"
              className="nav-user-trigger"
              onClick={() => setDropdownOpen((prev) => !prev)}
              aria-expanded={dropdownOpen}
            >
              <span className="nav-email">{user?.email || 'user@constellia.com'}</span>
              <span className={`nav-caret ${dropdownOpen ? 'open' : ''}`}>▼</span>
            </button>

            {dropdownOpen && (
              <div className="nav-dropdown-menu">
                <Link to="/account" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                  Account
                </Link>
                <Link to="/settings" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                  Settings
                </Link>

                <div className="dropdown-divider" />

                <div className="dropdown-group">
                  <label className="dropdown-label">Language</label>
                  <select
                    value={language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="dropdown-select"
                  >
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="sw">Kiswahili</option>
                  </select>
                </div>

                <div className="dropdown-group">
                  <label className="dropdown-label">Mode</label>
                  <select
                    value={mode}
                    onChange={(e) => handleModeChange(e.target.value)}
                    className="dropdown-select"
                  >
                    <option value="default">Default</option>
                    <option value="simple">Simple</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>

                <div className="dropdown-divider" />

                <button
                  type="button"
                  onClick={handleLogout}
                  className="dropdown-item dropdown-logout"
                >
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
